import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { searchProducts } from "../../../api/product";
import ProductCard from "./ProductCard/ProductCard";
import axiosInstance from "../../../api/axiosInstance";
import { ApiResponse } from "../../../types/Api";
import {
  X,
  SlidersHorizontal,
  ChevronDown,
  ChevronRight,
  FolderTree,
} from "lucide-react";

const PAGE_SIZE = 24;

/* ─── Types ───────────────────────────────────────────── */
type FacetValue = { value: string; count: number };
type Facet = { name: string; values: FacetValue[] };

type ProductItem = {
  id: number;
  ad: string;

  fiyat: number;
  paraBirimi?: string;

  fotoMain?: string | null;
  fotoList?: string[];

  kategoriId?: number;
  kategoriAd?: string | null;

  // pricing
  listeFiyati?: number | null;
  indirimliFiyat?: number | null;
  satisFiyati?: number | null;
  indirimAktifMi?: boolean | null;

  oneCikanmi?: boolean | null;
  stokAdedi?: number | null;
};

type SearchResp = {
  total: number;
  items: ProductItem[];
  facets?: Facet[];
};

type Category = {
  id: number;
  ad: string;
  ustKategoriId: number | null;
  slug: string;
  siraNo: number;
  aktifMi: boolean;
};

type CategoryNode = Category & { children: CategoryNode[] };

async function fetchCategories(): Promise<Category[]> {
  try {
    const res = await axiosInstance.post<ApiResponse<Category[]>>("/Kategori/listele", {});
    const payload = res.data;
    if (payload?.success && Array.isArray(payload.data)) return payload.data;
    return [];
  } catch {
    return [];
  }
}

function buildCategoryTree(list: Category[]): CategoryNode[] {
  const map = new Map<number, CategoryNode>();
  const roots: CategoryNode[] = [];

  list.forEach((c) => map.set(c.id, { ...c, children: [] }));

  map.forEach((node) => {
    if (node.ustKategoriId && map.has(node.ustKategoriId)) {
      map.get(node.ustKategoriId)!.children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (nodes: CategoryNode[]) => {
    nodes.sort(
      (a, b) =>
        (a.siraNo ?? 0) - (b.siraNo ?? 0) ||
        a.ad.localeCompare(b.ad, "tr")
    );
    nodes.forEach((n) => sortNodes(n.children));
  };
  sortNodes(roots);

  return roots;
}

/* ─── Helpers: attr parse/build ───────────────────────── */
function parseAttr(attrStr: string) {
  const map: Record<string, string[]> = {};
  if (!attrStr) return map;

  attrStr.split("|").forEach((pair) => {
    const [k, vs] = pair.split(":");
    if (!k || !vs) return;
    map[k] = vs.split(",").map((x) => x.trim()).filter(Boolean);
  });

  return map;
}

function buildAttr(map: Record<string, string[]>) {
  const s = Object.entries(map)
    .filter(([, vs]) => Array.isArray(vs) && vs.length > 0)
    .map(([k, vs]) => `${k}:${vs.join(",")}`)
    .join("|");
  return s;
}

/* ────────────────────────────────────────────────────── */
const Products: React.FC = () => {
  const [sp, setSp] = useSearchParams();

  // UI
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // data
  const [loading, setLoading] = useState(false);
  const [resp, setResp] = useState<SearchResp | undefined>();
  const [error, setError] = useState<string>();

  // categories
  const [cats, setCats] = useState<Category[]>([]);
  const [catsLoading, setCatsLoading] = useState(false);

  // category tree expand state
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  /* ── URL -> State ───────────────────────────────────── */
  const q = sp.get("search") || "";
  const sort = sp.get("sort") || "new";
  const page = Math.max(1, Number(sp.get("page") || 1));

  const minPrice = sp.get("min") ? Number(sp.get("min")) : undefined;
  const maxPrice = sp.get("max") ? Number(sp.get("max")) : undefined;

  const catId = sp.get("cat") ? Number(sp.get("cat")) : undefined;

  const attrStr = sp.get("attr") || "";
  const attr = useMemo(() => parseAttr(attrStr), [attrStr]);

  /* ── URL helper’ları ────────────────────────────────── */
  const applyParam = (patch: Record<string, string | number | undefined>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => {
      if (v === undefined || v === "" || v === null) next.delete(k);
      else next.set(k, String(v));
    });

    // filtre değişince sayfayı 1’e çek
    if (
      "search" in patch ||
      "min" in patch ||
      "max" in patch ||
      "attr" in patch ||
      "cat" in patch
    ) {
      next.set("page", "1");
    }

    setSp(next, { replace: false });
  };

  const clearAll = () => setSp(new URLSearchParams(), { replace: false });

  /* ── Attr toggle (React/URL senkron) ────────────────── */
  const toggleAttr = (name: string, value: string) => {
    const now = parseAttr(sp.get("attr") || "");
    const set = new Set(now[name] || []);
    if (set.has(value)) set.delete(value);
    else set.add(value);

    if (set.size === 0) delete now[name];
    else now[name] = Array.from(set);

    const nextStr = buildAttr(now);
    applyParam({ attr: nextStr || undefined });
  };

  /* ── Category toggle (tek seçim) ────────────────────── */
  const toggleCategory = (id: number) => {
    const selected = catId === id;
    applyParam({ cat: selected ? undefined : id });
  };

  /* ── Kategorileri çek ───────────────────────────────── */
  useEffect(() => {
    (async () => {
      setCatsLoading(true);
      const list = await fetchCategories();
      setCats(list);
      setCatsLoading(false);
    })();
  }, []);

  const categoryTree = useMemo(() => buildCategoryTree(cats), [cats]);

  // Root kategoriler ilk başta açık
  useEffect(() => {
    if (!cats.length) return;
    setExpanded((prev) => {
      const next = { ...prev };
      cats.forEach((c) => {
        if (c.ustKategoriId == null && next[c.id] === undefined) next[c.id] = true;
      });
      return next;
    });
  }, [cats]);

  const toggleExpand = (id: number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  /* ── Ürün araması ───────────────────────────────────── */
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(undefined);

      try {
        const body: any = {
          q: q || undefined,
          minPrice,
          maxPrice,
          attr,
          sort,
          page,
          pageSize: PAGE_SIZE,
          categoryId: catId && Number.isFinite(catId) ? catId : undefined,
        };

        const api = await searchProducts(body);
        if (!api?.success) {
          setError(api?.errorDetail || "Ürünler yüklenemedi");
          setResp(undefined);
          return;
        }

        const d: any = api.data || {};
        const normalized: SearchResp = {
          total: d.total ?? d.Total ?? 0,
          items: d.items ?? d.Items ?? [],
          facets: d.facets ?? d.Facets ?? [],
        };

        setResp(normalized);
      } catch (e: any) {
        setError(e?.message || "Ürünler yüklenemedi");
        setResp(undefined);
      } finally {
        setLoading(false);
      }
    })();
  }, [q, minPrice, maxPrice, attrStr, sort, page, catId]);

  const totalPages = resp ? Math.max(1, Math.ceil((resp.total || 0) / PAGE_SIZE)) : 1;

  const hasAnyFilter =
    !!q ||
    minPrice !== undefined ||
    maxPrice !== undefined ||
    !!catId ||
    Object.keys(attr).length > 0;

  /* ───────────────── UI ───────────────── */
  return (
    <div className="bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 py-5 md:py-7">
        {/* Header / Summary */}
        <div className="rounded-2xl border bg-white p-4 md:p-5 shadow-sm">
          <div className="flex items-start justify-between gap-3">


            {/* Mobile Filter Button */}
            <div className="flex items-center gap-2 md:hidden">
              <button
                type="button"
                onClick={() => setMobileFiltersOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl border bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm active:scale-[0.98]"
              >
                <SlidersHorizontal className="w-4 h-4" />
                Filtreler
              </button>
            </div>
          </div>

          {/* chips */}
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {minPrice !== undefined && (
              <Chip onRemove={() => applyParam({ min: undefined })}>
                Min: {minPrice}
              </Chip>
            )}
            {maxPrice !== undefined && (
              <Chip onRemove={() => applyParam({ max: undefined })}>
                Max: {maxPrice}
              </Chip>
            )}
            {catId && (
              <Chip onRemove={() => applyParam({ cat: undefined })}>
                {cats.find((x) => x.id === catId)?.ad || `Kategori #${catId}`}
              </Chip>
            )}
            {Object.entries(attr).flatMap(([name, vals]) =>
              vals.map((v) => (
                <Chip key={`${name}:${v}`} onRemove={() => toggleAttr(name, v)}>
                  {name}: {v}
                </Chip>
              ))
            )}

          </div>

          {error && (
            <div className="mt-3 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
              {error}
            </div>
          )}
        </div>

        {/* Top bar: sort + count (mobile + desktop) */}
        <div className="mt-4 flex items-center justify-between rounded-xl border bg-white px-3 py-2 shadow-sm">
          <div className="text-sm text-slate-600 gap-3 flex">
            {loading ? "Yükleniyor…" : `${resp?.total ?? 0} sonuç`} 
            {hasAnyFilter && (
              <button
                type="button"
                onClick={clearAll}
                className="text-sm font-semibold text-slate-600 underline hover:text-slate-900"
              >
                Filtreyi Temizle
              </button>
            )}
          </div>

          <select
            className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm font-semibold text-slate-900 outline-none"
            value={sort}
            onChange={(e) => applyParam({ sort: e.target.value })}
          >
            <option value="new">En Yeni</option>
            <option value="price_asc">Fiyat (Artan)</option>
            <option value="price_desc">Fiyat (Azalan)</option>
          </select>
        </div>

        <div className="mt-4 grid grid-cols-12 gap-6">
          {/* Desktop Filters */}
          <aside className="hidden md:block md:col-span-3">
            <FiltersPanel
              catsLoading={catsLoading}
              categoryTree={categoryTree}
              expanded={expanded}
              toggleExpand={toggleExpand}
              catId={catId}
              toggleCategory={toggleCategory}
              minPrice={minPrice}
              maxPrice={maxPrice}
              applyParam={applyParam}
              facets={resp?.facets || []}
              attr={attr}
              toggleAttr={toggleAttr}
            />
          </aside>

          {/* Products */}
          <section className="col-span-12 md:col-span-9">
            {loading ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {Array.from({ length: 9 }).map((_, i) => (
                  <div key={i} className="rounded-2xl border bg-white overflow-hidden">
                    <div className="aspect-square animate-pulse bg-slate-100" />
                    <div className="p-4">
                      <div className="h-4 w-3/4 rounded bg-slate-100 animate-pulse mb-2" />
                      <div className="h-4 w-1/2 rounded bg-slate-100 animate-pulse" />
                    </div>
                  </div>
                ))}
              </div>
            ) : resp?.items?.length ? (
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-5">
                {resp.items.map((p) => (
                  <ProductCard
                    key={p.id}
                    id={Number(p.id)}
                    ad={p.ad}
                    fiyat={p.fiyat}
                    paraBirimi={p.paraBirimi || "TRY"}
                    img={p.fotoMain ?? p.fotoList?.[0] ?? null}
                    listeFiyati={p.listeFiyati ?? null}
                    indirimliFiyat={p.indirimliFiyat ?? null}
                    indirimAktifMi={p.indirimAktifMi ?? null}
                    oneCikanmi={p.oneCikanmi ?? null}
                    stokAdedi={p.stokAdedi ?? null}
                  />
                ))}
              </div>
            ) : (
              <div className="rounded-2xl border bg-white p-6 text-sm text-slate-600">
                Sonuç bulunamadı.
              </div>
            )}

            {/* Pagination */}
            {resp?.total && resp.total > PAGE_SIZE && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                {Array.from({ length: totalPages }).map((_, i) => {
                  const p = i + 1;
                  const active = p === page;
                  return (
                    <button
                      key={p}
                      className={[
                        "px-3 py-1.5 rounded-lg border text-sm font-semibold",
                        active
                          ? "bg-blue-600 border-blue-600 text-white"
                          : "bg-white border-slate-200 text-slate-900 hover:bg-slate-50",
                      ].join(" ")}
                      onClick={() => applyParam({ page: p })}
                    >
                      {p}
                    </button>
                  );
                })}
              </div>
            )}
          </section>
        </div>
      </div>

      {/* Mobile Filters Drawer */}
      <MobileFiltersDrawer
        open={mobileFiltersOpen}
        onClose={() => setMobileFiltersOpen(false)}
        title="Filtreler"
        footer={
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="flex-1 rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-900"
              onClick={clearAll}
              disabled={!hasAnyFilter}
            >
              Temizle
            </button>
            <button
              type="button"
              className="flex-1 rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white active:scale-[0.99]"
              onClick={() => setMobileFiltersOpen(false)}
            >
              Uygula
            </button>
          </div>
        }
      >
        <FiltersPanel
          catsLoading={catsLoading}
          categoryTree={categoryTree}
          expanded={expanded}
          toggleExpand={toggleExpand}
          catId={catId}
          toggleCategory={toggleCategory}
          minPrice={minPrice}
          maxPrice={maxPrice}
          applyParam={applyParam}
          facets={resp?.facets || []}
          attr={attr}
          toggleAttr={toggleAttr}
          mobile
        />
      </MobileFiltersDrawer>
    </div>
  );
};

/* ───────────────── Components ───────────────── */

const Chip: React.FC<{ onRemove: () => void; children: React.ReactNode }> = ({
  onRemove,
  children,
}) => (
  <span className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-2.5 py-1 text-xs font-semibold text-slate-800 shadow-sm">
    {children}
    <button
      type="button"
      onClick={onRemove}
      className="ml-0.5 rounded-full px-1 text-slate-500 hover:text-slate-900"
      aria-label="Kaldır"
    >
      ✕
    </button>
  </span>
);

function MobileFiltersDrawer(props: {
  open: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  const { open, onClose, title, children, footer } = props;
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[80] md:hidden">
      {/* backdrop */}
      <button
        type="button"
        className="absolute inset-0 bg-black/35"
        onClick={onClose}
        aria-label="Kapat"
      />
      {/* panel */}
      <div className="absolute right-0 top-0 bottom-0 w-[88vw] max-w-[420px] bg-white shadow-2xl flex flex-col">
        <div className="px-4 py-4 border-b border-slate-200 flex items-center justify-between">
          <div className="text-sm font-normal  text-slate-900">{title}</div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-xl border border-slate-200 bg-white p-2 text-slate-900"
            aria-label="Kapat"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-auto p-4">{children}</div>

        {footer && (
          <div className="p-4 border-t border-slate-200 bg-white">{footer}</div>
        )}
      </div>
    </div>
  );
}

function FiltersPanel(props: {
  catsLoading: boolean;
  categoryTree: CategoryNode[];
  expanded: Record<number, boolean>;
  toggleExpand: (id: number) => void;
  catId?: number;
  toggleCategory: (id: number) => void;

  minPrice?: number;
  maxPrice?: number;
  applyParam: (patch: Record<string, string | number | undefined>) => void;

  facets: Facet[];
  attr: Record<string, string[]>;
  toggleAttr: (name: string, value: string) => void;

  mobile?: boolean;
}) {
  const {
    catsLoading,
    categoryTree,
    expanded,
    toggleExpand,
    catId,
    toggleCategory,
    minPrice,
    maxPrice,
    applyParam,
    facets,
    attr,
    toggleAttr,
    mobile,
  } = props;

  return (
    <div className="space-y-4">
      {/* Categories */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-xl bg-slate-100 text-slate-700">
              <FolderTree className="w-4 h-4" />
            </span>
            <div className="font-normal  text-slate-900">Kategoriler</div>
          </div>

          {catId ? (
            <button
              type="button"
              className="text-xs font-semibold text-slate-600 underline hover:text-slate-900"
              onClick={() => applyParam({ cat: undefined })}
            >
              Seçimi temizle
            </button>
          ) : null}
        </div>

        <div className={mobile ? "max-h-[48vh] overflow-auto pr-1" : "max-h-[420px] overflow-auto pr-1"}>
          {catsLoading ? (
            <div className="text-sm text-slate-600">Kategoriler yükleniyor…</div>
          ) : categoryTree.length === 0 ? (
            <div className="text-sm text-slate-600">Kategori bulunamadı.</div>
          ) : (
            categoryTree.map((node) => (
              <CategoryTreeRow
                key={node.id}
                node={node}
                depth={0}
                selectedCatId={catId ?? null}
                expanded={expanded}
                onToggleExpand={toggleExpand}
                onSelect={toggleCategory}
              />
            ))
          )}
        </div>
      </div>

      {/* Price */}
      <div className="rounded-2xl border bg-white p-4 shadow-sm">
        <div className="font-normal  text-slate-900">Fiyat</div>
        <div className="mt-3 grid grid-cols-2 gap-2">
          <input
            type="number"
            placeholder="Min"
            defaultValue={minPrice ?? ""}
            onBlur={(e) => applyParam({ min: e.target.value || undefined })}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20"
          />
          <input
            type="number"
            placeholder="Max"
            defaultValue={maxPrice ?? ""}
            onBlur={(e) => applyParam({ max: e.target.value || undefined })}
            className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-900 outline-none focus:ring-2 focus:ring-blue-500/20"
          />
        </div>
        <div className="mt-2 text-xs text-slate-500">
          Kutudan çıkınca (blur) otomatik uygulanır.
        </div>
      </div>

      {/* Facets */}
      {facets?.length > 0 && (
        <div className="rounded-2xl border bg-white p-4 shadow-sm">
          <div className="font-normal  text-slate-900 mb-2">Diğer Filtreler</div>

          <div className="space-y-3">
            {facets.map((f) => (
              <FacetAccordion key={f.name} title={f.name}>
                <div className="max-h-56 overflow-auto pr-1 space-y-2">
                  {f.values.map((v) => {
                    const checked = (attr[f.name] || []).includes(v.value);
                    return (
                      <label
                        key={v.value}
                        className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-3 py-2 hover:bg-slate-50"
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => toggleAttr(f.name, v.value)}
                          className="h-4 w-4"
                        />
                        <span className="text-sm font-semibold text-slate-800">
                          {v.value}
                        </span>
                        <span className="ml-auto text-xs font-semibold text-slate-500">
                          {v.count}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </FacetAccordion>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FacetAccordion(props: {
  title: string;
  children: React.ReactNode;
}) {
  const { title, children } = props;
  const [open, setOpen] = useState(true);

  return (
    <div className="rounded-2xl border border-slate-200 overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 text-slate-900"
      >
        <span className="text-sm font-normal ">{title}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="p-3 bg-white">{children}</div>}
    </div>
  );
}

type CategoryTreeRowProps = {
  node: CategoryNode;
  depth: number;
  selectedCatId: number | null;
  expanded: Record<number, boolean>;
  onToggleExpand: (id: number) => void;
  onSelect: (id: number) => void;
};

const CategoryTreeRow: React.FC<CategoryTreeRowProps> = ({
  node,
  depth,
  selectedCatId,
  expanded,
  onToggleExpand,
  onSelect,
}) => {
  const hasChildren = node.children && node.children.length > 0;
  const isExpanded = expanded[node.id] ?? (depth === 0);
  const isSelected = selectedCatId === node.id;

  return (
    <div>
      <div
        className={[
          "flex items-center rounded-xl px-2 py-2 cursor-pointer",
          "hover:bg-slate-50",
          isSelected ? "bg-blue-50" : "",
        ].join(" ")}
        style={{ marginLeft: depth * 10 }}
        onClick={() => onSelect(node.id)}
      >
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (hasChildren) onToggleExpand(node.id);
          }}
          className={[
            "mr-2 inline-flex items-center justify-center",
            "w-7 h-7 rounded-xl border border-slate-200 bg-white",
            hasChildren ? "" : "opacity-0 pointer-events-none",
          ].join(" ")}
          aria-label="Alt kategorileri aç/kapat"
        >
          {hasChildren ? (
            isExpanded ? (
              <ChevronDown className="w-4 h-4 text-slate-700" />
            ) : (
              <ChevronRight className="w-4 h-4 text-slate-700" />
            )
          ) : null}
        </button>

        <span className="flex-1 min-w-0">
          <span
            className={[
              "block truncate text-sm",
              isSelected ? "font-normal  text-slate-900" : "font-semibold text-slate-700",
            ].join(" ")}
            title={node.ad}
          >
            {node.ad}
          </span>
        </span>

        {isSelected && (
          <span className="ml-2 rounded-full bg-gradient-to-r from-blue-600 to-teal-500 px-2 py-1 text-[10px] font-normal  text-white">
            Seçili
          </span>
        )}
      </div>

      {hasChildren && isExpanded && (
        <div className="mt-1">
          {node.children.map((child) => (
            <CategoryTreeRow
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedCatId={selectedCatId}
              expanded={expanded}
              onToggleExpand={onToggleExpand}
              onSelect={onSelect}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default Products;
